import type { AudioBus, Voice } from './audioBus';
import { midi, noise, tone } from './synth';

/**
 * Seen from the local player; watching two AIs, a decided game counts as a win for someone.
 * `decided` is a duel between two people at one device: won, without saying for whom.
 */
export type Outcome = 'win' | 'lose' | 'draw' | 'decided';

/** A fight on the board is decided (rolled, or fought out): an impact and a short chord. */
export function fightResolved(bus: AudioBus): void {
  const v = bus.voice('sting-fight', { gain: 0.6, reverb: 0.35, priority: 'high' });
  if (!v) return;
  let end = tone(v, {
    freq: 140,
    sweep: { to: 40, seconds: 0.12 },
    seconds: 0.06,
    env: { attack: 0.003, release: 0.35 },
    gain: 0.8,
  });
  end = Math.max(
    end,
    noise(v, {
      seconds: 0.03,
      env: { attack: 0.002, release: 0.25 },
      filter: { type: 'lowpass', freq: 1500 },
      gain: 0.4,
    }),
  );
  end = Math.max(end, chord(v, [57, 64, 69], 0.2, 0.35, 0.14));
  v.end(end);
}

/**
 * A fanfare, a lament, a hollow note or a call; for a duel (`fight`) or the whole game (`game`).
 */
export function outcomeSting(bus: AudioBus, outcome: Outcome, scale: 'fight' | 'game'): void {
  const v = bus.voice(`sting-${outcome}`, { gain: 0.6, reverb: 0.45, priority: 'high' });
  if (!v) return;
  const big = scale === 'game';
  const step = big ? 0.17 : 0.13;
  let end = 0;
  const play = (notes: number[], at: number, seconds: number, gain: number) => {
    end = Math.max(end, chord(v, notes, at, seconds, gain));
  };
  switch (outcome) {
    case 'win': {
      // Brass climbing to the octave, then the chord held; the game gets one note more.
      const line = big ? [60, 64, 67, 72, 79] : [60, 64, 67, 72];
      line.forEach((note, i) =>
        play([note], i * step, i === line.length - 1 ? 0.6 : step * 0.9, 0.2),
      );
      play(big ? [48, 55, 64, 67] : [48, 55, 64], step * line.length, big ? 1.2 : 0.7, 0.14);
      break;
    }
    case 'lose':
      // Three steps down into a minor chord.
      [67, 63, 60].forEach((note, i) => play([note], i * step * 1.6, step * 1.4, 0.18));
      play([48, 51, 55], step * 1.6 * 3, big ? 1.4 : 0.8, 0.12);
      break;
    case 'draw':
      // The same hollow fifth, twice.
      play([55, 62], 0, 0.3, 0.16);
      play([55, 62], 0.45, big ? 1 : 0.6, 0.16);
      break;
    case 'decided':
      // A herald's call rather than a climb or a fall: the fifth, the octave, the chord held.
      play([67], 0, step * 0.9, 0.2);
      play([72], step, step * 0.9, 0.2);
      play(big ? [48, 55, 64, 67] : [48, 55, 64], step * 2, big ? 1.2 : 0.7, 0.14);
      break;
  }
  v.end(end);
}

/** Sawtooth notes through a lowpass with a little vibrato: a small brass section. */
function chord(v: Voice, notes: number[], at: number, seconds: number, gain: number): number {
  let end = 0;
  for (const note of notes)
    end = Math.max(
      end,
      tone(v, {
        type: 'sawtooth',
        freq: midi(note),
        seconds,
        at,
        env: { attack: 0.02, decay: 0.1, sustain: 0.8, release: 0.25 },
        gain,
        filter: { type: 'lowpass', freq: 2200, q: 0.7 },
        vibrato: { hz: 5, cents: 5 },
      }),
    );
  return end;
}
